import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Search, MapPin, Calendar, Users } from 'lucide-react';

const ManageEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (eventId, title) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'événement "${title}" ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        alert('Événement supprimé avec succès');
        fetchEvents();
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Disponible': return '#2c5f7d';
      case 'Complet': return '#8b4f6d';
      case 'Annulé': return '#c41e3a';
      case 'Terminé': return '#999';
      default: return '#666';
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      'Gala': '#8b4f6d',
      'Festival': '#2c5f7d',
      'Fashion': '#8b7355'
    };
    return colors[type] || '#666666';
  };

  return (
    <AdminLayout>
      <div style={{ padding: '32px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px'
        }}>
          <h1 className="hero-medium">Gestion des Événements</h1>
          <button
            onClick={() => navigate('/admin/events/add')}
            className="btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Plus size={20} />
            Ajouter un événement
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: '24px', position: 'relative' }}>
          <Search size={20} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-secondary)'
          }} />
          <input
            type="text"
            placeholder="Rechercher un événement (titre, lieu, type)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 40px',
              border: '1px solid var(--border-light)',
              borderRadius: '0px',
              fontSize: '14px',
              fontFamily: 'inherit',
              background: 'var(--bg-primary)'
            }}
          />
        </div>

        {loading ? (
          <p>Chargement...</p>
        ) : filteredEvents.length === 0 ? (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)'
          }}>
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              {searchTerm ? 'Aucun événement ne correspond à votre recherche' : 'Aucun événement enregistré'}
            </p>
          </div>
        ) : (
          <div style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)',
            overflowX: 'auto'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <th className="body-regular" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Titre</th>
                  <th className="body-regular" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Type</th>
                  <th className="body-regular" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Date</th>
                  <th className="body-regular" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Lieu</th>
                  <th className="body-regular" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Places</th>
                  <th className="body-regular" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Prix membre</th>
                  <th className="body-regular" style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Statut</th>
                  <th className="body-regular" style={{ padding: '16px', textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((event) => (
                  <tr key={event.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td className="body-regular" style={{ padding: '16px', fontWeight: 600 }}>
                      {event.title}
                    </td>
                    <td className="body-small" style={{ padding: '16px' }}>
                      <span style={{
                        padding: '4px 8px',
                        background: getTypeColor(event.type),
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 600
                      }}>
                        {event.type}
                      </span>
                    </td>
                    <td className="body-small" style={{ padding: '16px', color: 'var(--text-secondary)' }}>
                      {event.date}
                    </td>
                    <td className="body-small" style={{ padding: '16px' }}>
                      {event.location}
                    </td>
                    <td className="body-small" style={{ padding: '16px' }}>
                      {event.seats_sold} / {event.total_seats}
                    </td>
                    <td className="body-small" style={{ padding: '16px', fontWeight: 600 }}>
                      {event.price_member?.toLocaleString('fr-FR')} €
                    </td>
                    <td className="body-small" style={{ padding: '16px' }}>
                      <span style={{
                        padding: '4px 12px',
                        background: getStatusColor(event.status),
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {event.status}
                      </span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => navigate(`/admin/events/edit/${event.id}`)}
                          style={{
                            padding: '8px',
                            background: 'transparent',
                            border: '1px solid var(--border-light)',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)'
                          }}
                          title="Modifier"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(event.id, event.title)}
                          style={{
                            padding: '8px',
                            background: 'transparent',
                            border: '1px solid #c41e3a',
                            cursor: 'pointer',
                            color: '#c41e3a'
                          }}
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="body-small" style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>
          {filteredEvents.length} événement(s) {searchTerm && 'trouvé(s)'}
        </p>
      </div>
    </AdminLayout>
  );
};

export default ManageEvents;
